import type { Schema } from '@dealdesk/api-schema';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { idempotent } from '@/shared/api/headers';

import { employerKeys } from './keys';

function setEmployer(qc: QueryClient, e: Schema<'Employer'>) {
  qc.setQueryData(employerKeys.detail(e.id), e);
  void qc.invalidateQueries({ queryKey: employerKeys.lists() });
  void qc.invalidateQueries({ queryKey: employerKeys.duplicates() });
  void qc.invalidateQueries({ queryKey: [...employerKeys.all, 'enlistments'] });
  // Deal headers show the employer's category at the deal's bank.
  void qc.invalidateQueries({ queryKey: ['deals'] });
}

export function useCreateEmployer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'EmployerInput'>) => unwrap(api.POST('/employers', { body, params: { header: idempotent() } })),
    onSuccess: (e) => setEmployer(qc, e),
  });
}

export function useUpdateEmployer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ version, body }: { version: string; body: Schema<'EmployerInput'> }) => unwrap(api.PATCH('/employers/{employerId}', { params: { path: { employerId: id }, header: { 'If-Match': version } }, body })),
    onSuccess: (e) => setEmployer(qc, e),
  });
}

export function useClassifyEmployer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schema<'ClassificationInput'>) => unwrap(api.POST('/employers/{employerId}/classifications', { params: { path: { employerId: id } }, body })),
    onSuccess: (e) => setEmployer(qc, e),
  });
}

export function useRequestEnlistment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { bankId: string; note?: string }) => unwrap(api.POST('/employers/{employerId}/enlistments', { params: { path: { employerId: id }, header: idempotent() }, body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: employerKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: [...employerKeys.all, 'enlistments'] });
      void qc.invalidateQueries({ queryKey: employerKeys.lists() });
    },
  });
}

export function useUpdateEnlistment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { status: Schema<'EnlistmentStatus'>; note?: string; categoryCode?: string } }) => unwrap(api.POST('/enlistments/{enlistmentId}/status', { params: { path: { enlistmentId: id } }, body })),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: employerKeys.detail(r.employerId) });
      void qc.invalidateQueries({ queryKey: [...employerKeys.all, 'enlistments'] });
      void qc.invalidateQueries({ queryKey: employerKeys.lists() });
      void qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useMergeEmployers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { sourceId: string; targetId: string }) => unwrap(api.POST('/employers/merge', { body, params: { header: idempotent() } })),
    onSuccess: (e) => {
      setEmployer(qc, e);
      void qc.invalidateQueries({ queryKey: employerKeys.all });
    },
  });
}

/** Links a "not in master" employment to an existing employer (optionally saving the alias) or to a new employer. */
export function useResolveUnresolvedEmployment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ employmentId, body }: { employmentId: string; body: Schema<'UnresolvedEmploymentResolve'> }) =>
      unwrap(api.POST('/employers/unresolved/{employmentId}/resolve', { params: { path: { employmentId } }, body })),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: employerKeys.unresolved() });
      void qc.invalidateQueries({ queryKey: employerKeys.lists() });
      void qc.invalidateQueries({ queryKey: employerKeys.detail(r.employerId) });
      // The customer's employments and the deal forms of its open deals show the employer now.
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['deals'] });
    },
    // Already resolved by someone else (409): refresh the list.
    onError: () => void qc.invalidateQueries({ queryKey: employerKeys.unresolved() }),
  });
}
